import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SafetyTrainingProvider, useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import { hasPermission, Permission } from '../../utils/permissions';
import { AdminDashboardView } from './AdminDashboardView';
import { ActionLauncherModal } from './ActionLauncherModal';
import MasterTopicListPage from './MasterTopicListPage';
import { AddEditTopicModal } from './AddEditTopicModal';
import { ImportTopicsModal } from './ImportTopicsModal';
import { BulkAssignCategoryModal } from './BulkAssignCategoryModal';
import PlanBuilderPage from './PlanBuilderPage';
import ScheduleBuilderPage from './ScheduleBuilderPage';
import { EnrollmentModal } from './EnrollmentModal';
import { ManageEnrollmentModal } from './ManageEnrollmentModal';
import UserTrainingProfilePage from './UserTrainingProfilePage';
import MyTrainingPage from './MyTrainingPage';
import AffidavitModal from './AffidavitModal';
import { listAllLibraryPlanTemplates } from '../../graphql/resourceLibraryQueries';
import { SaveToLibraryView } from './SaveToLibraryView';
import { ImportFromLibraryView } from './ImportFromLibraryView';
import type { SafetyTrainingView } from '../../types/trainingTypes';
import '../../css/SafetyTrainingDashboard.css';
import { Btn, Card, PageHeader, TabNav } from '../ui';

/** Small wrapper that resolves planId from context when not provided */
const EnrollmentModalWrapper = ({
    isOpen,
    onClose,
    requestedPlanId,
}: {
    isOpen: boolean;
    onClose: () => void;
    requestedPlanId: string;
}) => {
    const { plans } = useSafetyTrainingContext();
    const planId = requestedPlanId || plans.find((p) => p.status === 'ACTIVE')?.id || plans[0]?.id || '';

    if (!isOpen) return null;

    // No plans exist — show a message instead of the enrollment modal
    if (!planId) {
        return (
            <div className="alm-overlay" onClick={onClose}>
                <div className="alm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
                    <div className="alm-header">
                        <h3>User Enrollment</h3>
                        <button className="alm-close-btn" onClick={onClose} aria-label="Close">
                            ×
                        </button>
                    </div>
                    <div className="alm-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
                        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                            No training plans have been created yet. Create a plan first using
                            <strong> Design a Plan</strong>, then come back to enroll users.
                        </p>
                    </div>
                    <div className="alm-footer">
                        <button className="alm-footer-close-btn" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return <EnrollmentModal isOpen={isOpen} onClose={onClose} planId={planId} />;
};

type AdminTab = 'admin-dashboard' | 'my-training';

/**
 * Main container for the Safety & Training module.
 * Rendered when page === "safety training" in Dashboard.tsx.
 *
 * - Wraps content in SafetyTrainingProvider context
 * - Manages internal view state using SafetyTrainingView discriminated union
 * - Admin users: tab bar with "Admin Dashboard" and "My Training" tabs (dual-access)
 * - Staff users: renders only My Training (no tab bar)
 * - Uses hasPermission(companyOwner, companyUser, Permission.MANAGE_SAFETY_AND_TRAINING) for role check
 *
 * Validates: Requirements 20.3, 20.4, 23.1, 23.2
 */
export const SafetyTrainingDashboard = ({
    companyId: companyIdProp,
    templateMode = false,
}: { companyId?: string; templateMode?: boolean } = {}) => {
    const { companyOwner, companyUser, businessAdmin } = useUserContext();
    const isAdmin =
        templateMode || hasPermission(companyOwner, companyUser, Permission.MANAGE_SAFETY_AND_TRAINING, businessAdmin);

    const [searchParams, setSearchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState<AdminTab>('admin-dashboard');
    const [view, setView] = useState<SafetyTrainingView>(() => {
        // If navigated here with ?view=plan-builder, start directly on the plan builder
        if (searchParams.get('view') === 'plan-builder') {
            return { type: 'plan-builder' };
        }
        if (isAdmin && searchParams.get('view') === 'topics') {
            return { type: 'topics' };
        }
        return isAdmin ? { type: 'admin-dashboard' } : { type: 'my-training' };
    });
    const [showActionLauncher, setShowActionLauncher] = useState(false);
    const [actionLauncherPlanId, setActionLauncherPlanId] = useState<string | undefined>(undefined);
    const [actionLauncherPlanName, setActionLauncherPlanName] = useState<string | undefined>(undefined);
    const [showAddEditTopic, setShowAddEditTopic] = useState(false);
    const [editTopicId, setEditTopicId] = useState<string | null>(null);
    const [showImportTopics, setShowImportTopics] = useState(false);
    const [bulkAssignTopicIds, setBulkAssignTopicIds] = useState<string[]>([]);
    const [showBulkAssign, setShowBulkAssign] = useState(false);
    const [showAffidavit, setShowAffidavit] = useState(false);
    const [affidavitUserId, setAffidavitUserId] = useState<string | undefined>(undefined);
    const [showEnrollment, setShowEnrollment] = useState(false);
    const [enrollmentPlanId, setEnrollmentPlanId] = useState<string>('');
    const [showManageEnrollment, setShowManageEnrollment] = useState(false);
    const [manageEnrollmentUserId, setManageEnrollmentUserId] = useState<string>('');
    const [manageEnrollmentUserName, setManageEnrollmentUserName] = useState<string>('');
    const [hasAssignedLibraryPlans, setHasAssignedLibraryPlans] = useState(false);

    // Clear the ?view= query param after consuming it on initial render
    useEffect(() => {
        if (searchParams.get('view')) {
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check if company has assigned library plan templates (skip in template mode)
    const companyId = companyIdProp || companyUser?.companyId || companyOwner?.companyId;
    useEffect(() => {
        if (!companyId || templateMode) return;
        const checkLibraryPlans = async () => {
            try {
                const plans = await listAllLibraryPlanTemplates();
                const hasPlans = plans.some((p) =>
                    p.sharedWithCompanyIds?.includes(companyId),
                );
                setHasAssignedLibraryPlans(hasPlans);
            } catch {
                // Silently fail — card just won't show
            }
        };
        checkLibraryPlans();
    }, [companyId]);

    const adminTabs: { id: AdminTab; label: string }[] = templateMode
        ? [{ id: 'admin-dashboard', label: 'Plans & Templates' }]
        : [
              { id: 'admin-dashboard', label: 'Admin Dashboard' },
              { id: 'my-training', label: 'My Training' },
          ];

    const handleTabChange = (tabId: AdminTab) => {
        setActiveTab(tabId);
        setView({ type: tabId });
    };

    const handleNavigate = (newView: SafetyTrainingView) => {
        // Enrollment and affidavit are modals, not views — open them directly
        if (newView.type === 'enrollment') {
            setEnrollmentPlanId(newView.planId || '');
            setShowEnrollment(true);
            return;
        }
        if (newView.type === 'affidavit') {
            setAffidavitUserId(newView.userId);
            setShowAffidavit(true);
            return;
        }
        setView(newView);
    };

    const handleBackToDashboard = () => {
        if (isAdmin) {
            setActiveTab('admin-dashboard');
            setView({ type: 'admin-dashboard' });
        } else {
            setView({ type: 'my-training' });
        }
    };

    const renderView = () => {
        switch (view.type) {
            case 'admin-dashboard':
                return (
                    <AdminDashboardView
                        onNavigate={handleNavigate}
                        onOpenActionLauncher={(planId, planName) => {
                            setActionLauncherPlanId(planId);
                            setActionLauncherPlanName(planName);
                            setShowActionLauncher(true);
                        }}
                        templateMode={templateMode}
                    />
                );
            case 'my-training':
                if (templateMode) {
                    // Should never reach here in template mode, but guard anyway
                    handleBackToDashboard();
                    return null;
                }
                return (
                    <MyTrainingPage
                        onViewFullRecord={() => {
                            const userId = companyUser?.id ?? companyOwner?.id ?? '';
                            if (userId) {
                                setView({ type: 'user-profile', userId });
                            }
                        }}
                    />
                );
            case 'topics':
                return (
                    <MasterTopicListPage
                        onBack={handleBackToDashboard}
                        onOpenAddTopic={(topicId) => {
                            setEditTopicId(topicId ?? null);
                            setShowAddEditTopic(true);
                        }}
                        onOpenImport={() => setShowImportTopics(true)}
                        onOpenBulkAssign={(ids) => {
                            setBulkAssignTopicIds(ids);
                            setShowBulkAssign(true);
                        }}
                        initialPlanFilter={view.filterPlanId}
                    />
                );
            case 'plan-builder':
                return (
                    <PlanBuilderPage
                        onBack={handleBackToDashboard}
                        editPlanId={view.planId}
                        templateMode={templateMode}
                        onImportFromLibrary={() => setView({ type: 'import-from-library' })}
                    />
                );
            case 'schedule-builder':
                return (
                    <ScheduleBuilderPage
                        onBack={handleBackToDashboard}
                        planId={view.planId}
                        templateMode={templateMode}
                    />
                );
            case 'user-profile':
                if (templateMode) return null;
                return (
                    <UserTrainingProfilePage
                        userId={view.userId}
                        onBack={handleBackToDashboard}
                        onOpenManageEnrollment={(uid, uname) => {
                            setManageEnrollmentUserId(uid);
                            setManageEnrollmentUserName(uname);
                            setShowManageEnrollment(true);
                        }}
                        onOpenAffidavit={(uid) => {
                            setAffidavitUserId(uid);
                            setShowAffidavit(true);
                        }}
                    />
                );
            case 'enrollment':
                return null;
            case 'affidavit':
                return null;
            case 'save-to-library':
                return <SaveToLibraryView onBack={handleBackToDashboard} />;
            case 'import-from-library':
                return (
                    <ImportFromLibraryView
                        onBack={handleBackToDashboard}
                        companyId={companyId || ''}
                        onImportComplete={() => {
                            handleBackToDashboard();
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <SafetyTrainingProvider companyId={companyIdProp}>
            <div className="page-container st-dashboard-container">
                {/* Template mode banner */}
                {templateMode && (
                    <div className="st-template-mode-banner">
                        <span>📋</span>
                        <span>
                            Template Mode — Topics and plans are saved to the TotalEquipIQ library and can be shared to any
                            company.
                        </span>
                    </div>
                )}

                {/* Module-level header buttons — not plan-specific */}
                <PageHeader
                    title={templateMode ? 'Training Template Builder' : 'Safety & Training'}
                    actions={
                        isAdmin && (view.type === 'admin-dashboard' || view.type === 'my-training') ? (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <Btn
                                    variant="secondary-outlined"
                                    buttonText="Master Topic List"
                                    buttonOnClick={() => handleNavigate({ type: 'topics' })}
                                />
                                <Btn
                                    variant="secondary-outlined"
                                    buttonText="Manage Plans"
                                    buttonOnClick={() => handleNavigate({ type: 'plan-builder' })}
                                />
                            </div>
                        ) : undefined
                    }
                />

                {/* Admin tab bar (only for admin users) */}
                {isAdmin && (view.type === 'admin-dashboard' || view.type === 'my-training') && (
                    <Card variant="card-sm" cardClasses="st-dashboard-tabs-card">
                        <TabNav
                            variant="pills"
                            ariaLabel="Safety and training sections"
                            className="tab-nav-stand-alone st-dashboard-tabs"
                            id="safety-training-tabs"
                            tabs={adminTabs}
                            activeTabId={activeTab}
                            onTabChange={(tabId) => handleTabChange(tabId as AdminTab)}
                        />
                    </Card>
                )}

                {/* View content */}
                {renderView()}
            </div>

            {/* Action Launcher Modal */}
            <ActionLauncherModal
                isOpen={showActionLauncher}
                onClose={() => {
                    setShowActionLauncher(false);
                    setActionLauncherPlanId(undefined);
                    setActionLauncherPlanName(undefined);
                }}
                onSelect={handleNavigate}
                hasAssignedLibraryPlans={hasAssignedLibraryPlans}
                planId={actionLauncherPlanId}
                planName={actionLauncherPlanName}
            />

            {/* Add/Edit Topic Modal */}
            <AddEditTopicModal
                isOpen={showAddEditTopic}
                onClose={() => {
                    setShowAddEditTopic(false);
                    setEditTopicId(null);
                }}
                editTopicId={editTopicId}
            />

            {/* Import Topics Modal */}
            <ImportTopicsModal isOpen={showImportTopics} onClose={() => setShowImportTopics(false)} />

            {/* Bulk Assign Category Modal */}
            <BulkAssignCategoryModal
                isOpen={showBulkAssign}
                onClose={() => {
                    setShowBulkAssign(false);
                    setBulkAssignTopicIds([]);
                }}
                selectedTopicIds={bulkAssignTopicIds}
            />

            {/* Affidavit Modal */}
            <AffidavitModal
                isOpen={showAffidavit}
                onClose={() => {
                    setShowAffidavit(false);
                    setAffidavitUserId(undefined);
                }}
                userId={affidavitUserId}
            />

            {/* Enrollment Modal (per-plan) — not used in template mode */}
            {!templateMode && showEnrollment && (
                <EnrollmentModalWrapper
                    isOpen={showEnrollment}
                    onClose={() => {
                        setShowEnrollment(false);
                        setEnrollmentPlanId('');
                    }}
                    requestedPlanId={enrollmentPlanId}
                />
            )}

            {/* Manage Enrollment Modal (per-user) — not used in template mode */}
            {!templateMode && showManageEnrollment && manageEnrollmentUserId && (
                <ManageEnrollmentModal
                    isOpen={showManageEnrollment}
                    onClose={() => {
                        setShowManageEnrollment(false);
                        setManageEnrollmentUserId('');
                    }}
                    userId={manageEnrollmentUserId}
                    userName={manageEnrollmentUserName}
                />
            )}
        </SafetyTrainingProvider>
    );
};
